/**
 * The planner's WORKING DRAFT, in localStorage.
 *
 * This is not superseded by the server-side agendas (US-MB-5) — it is the layer under them.
 * /kit/planner is reachable without signing in, and a draft must survive a reload before anyone
 * has an account, so the draft stays local and "Save" copies it to the server. `serverId` is the
 * link: set once an agenda has been saved, so the next save updates instead of duplicating.
 *
 * The auto-timing math lives in @klapzo/kit-core (recomputeAgenda); this just holds the blocks.
 */
import type { AgendaBlockInput } from "@klapzo/kit-core";

const KEY = "klapzo.agenda.v1";

/**
 * The live room this draft is running its blocks in (US-MB-6, W12 fatia 1).
 *
 * Client state for the run, deliberately: nothing new is stored server-side for the agenda itself,
 * and the block→room link that DOES persist is `agenda_blocks.live_session_id`, written per block
 * when it actually runs. The code rides along because a session id cannot be turned back into one
 * (`GET /j/:code` maps code→id, never the reverse), and the code is what the room needs to see.
 */
export interface LiveSession {
  id: string;
  code: string;
}

export interface StoredAgenda {
  title: string;
  startClock: string;
  blocks: AgendaBlockInput[];
  /** id of the server copy, once saved. null/absent = draft only. */
  serverId?: string | null;
  /** null/absent = no room attached; the planner works exactly as it always did. */
  live?: LiveSession | null;
}

const EMPTY: StoredAgenda = { title: "Minha agenda", startClock: "09:00", blocks: [] };

export function loadAgenda(): StoredAgenda {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...EMPTY };
    const parsed = JSON.parse(raw) as StoredAgenda;
    return {
      title: parsed.title ?? EMPTY.title,
      startClock: parsed.startClock ?? EMPTY.startClock,
      blocks: parsed.blocks ?? [],
      serverId: parsed.serverId ?? null,
      live: parsedLive(parsed.live),
    };
  } catch {
    return { ...EMPTY };
  }
}

export function saveAgenda(a: StoredAgenda): void {
  // Quota errors are swallowed: a full localStorage should not take the planner down mid-edit.
  // The in-memory state is still correct, and the draft is not the system of record once saved.
  try {
    localStorage.setItem(KEY, JSON.stringify(a));
  } catch {
    /* ignore */
  }
}

/** A draft is user-writable storage: a half-written room would send `undefined` to live-api as a
 *  session id and 404 on every click, so both halves are checked before it counts as attached. */
function parsedLive(live: unknown): LiveSession | null {
  const l = live as { id?: unknown; code?: unknown } | null | undefined;
  return l && typeof l.id === "string" && l.id !== "" && typeof l.code === "string" && l.code !== ""
    ? { id: l.id, code: l.code }
    : null;
}

export function addBlock(method: { id: string; title: string; durationMin: number | null }): void {
  const a = loadAgenda();
  a.blocks.push({
    id: crypto.randomUUID(),
    title: method.title,
    durationMin: method.durationMin ?? 15,
    methodId: method.id,
  });
  saveAgenda(a);
}
